import { cx } from '../lib/cx';
import { Icon, type IconName } from '../icons/Icon';
import { nextThemePreference, useThemePreference, type ThemePreference } from '../lib/theme';

const MODE_ICON: Record<ThemePreference, IconName> = { light: 'sun', dark: 'moon', auto: 'monitor' };

export interface ThemeModeButtonProps {
  /** Visible name of each mode. The portal passes translated strings. */
  labels?: Record<ThemePreference, string>;
  /** Accessible name from the current mode's label. Keep the label in it,
   *  so the visible text stays part of the name. */
  getAriaLabel?: (modeLabel: string) => string;
  className?: string;
}

/**
 * Three-way theme switch as one button: each click moves to the next mode,
 * light → dark → auto → light, and the face shows the mode now in force.
 * Self-contained — reads and saves the preference through the theme store.
 */
export function ThemeModeButton({
  labels = { light: 'Light', dark: 'Dark', auto: 'Auto' },
  getAriaLabel = (modeLabel) => `Theme: ${modeLabel}`,
  className,
}: ThemeModeButtonProps) {
  const { preference, setPreference } = useThemePreference();
  return (
    <button
      type="button"
      className={cx('tc-btn', 'tc-btn--secondary', 'tc-btn--sm', 'tc-theme-mode', className)}
      onClick={() => setPreference(nextThemePreference(preference))}
      aria-label={getAriaLabel(labels[preference])}
      data-mode={preference}
    >
      <Icon name={MODE_ICON[preference]} size={16} />
      {labels[preference]}
    </button>
  );
}
